import { InteractionStatus } from "@azure/msal-browser";
import {
	AuthenticatedTemplate,
	MsalProvider,
	UnauthenticatedTemplate,
	useMsal,
} from "@azure/msal-react";
import type { ReactNode } from "react";
import { useState } from "react";

import { b2cMsalInstance } from "~/app/main/auth-config.ts";

import { LoadingSpinner } from "../components/loading-spinner/loading-spinner.tsx";
import { useInitializeMsalInstance } from "./b2c-utils.ts";

type B2CAuthProviderProps = {
	children: ReactNode;
	unauthenticated: ReactNode;
};

function B2CLoadingComponent() {
	return <LoadingSpinner isVisible={true} />;
}

function B2CContent({
	children,
	unauthenticated,
}: Readonly<B2CAuthProviderProps>) {
	const { inProgress } = useMsal();

	// IMPORTANT: only treat the *initial* auth bootstrap as "in progress".
	// `inProgress !== "none"` is true for every silent token acquisition that
	// the app's fetch hook makes on each API call. If we unmount the tree on
	// `"acquireToken"`, errored queries (e.g. a vendor hitting 403s) refetch
	// on the next mount, which kicks off another `acquireTokenSilent`, which
	// flips `inProgress` again — producing a remount/refetch loop that looks
	// like the whole page is reloading.
	const isInitialAuthInProgress =
		inProgress === InteractionStatus.Startup ||
		inProgress === InteractionStatus.HandleRedirect ||
		inProgress === InteractionStatus.Login ||
		inProgress === InteractionStatus.Logout;

	if (isInitialAuthInProgress) {
		return <B2CLoadingComponent />;
	}

	return (
		<>
			<AuthenticatedTemplate>{children}</AuthenticatedTemplate>
			<UnauthenticatedTemplate>{unauthenticated}</UnauthenticatedTemplate>
		</>
	);
}

export function B2CAuthProvider({
	children,
	unauthenticated,
}: Readonly<B2CAuthProviderProps>) {
	// `roles` is only consumed via the MSAL event-callback setter; the value
	// itself is intentionally not read in this provider.
	// eslint-disable-next-line react/hook-use-state -- setter is forwarded to MSAL callback; value is not needed in this component
	const [, setRoles] = useState<Set<string> | undefined>(undefined);

	const initializationStatus = useInitializeMsalInstance(
		b2cMsalInstance,
		undefined,
		setRoles,
	);
	let content: ReactNode = <B2CLoadingComponent />;
	if (initializationStatus === "initialized") {
		content = (
			<B2CContent unauthenticated={unauthenticated}>{children}</B2CContent>
		);
	} else if (initializationStatus === "error") {
		content = unauthenticated;
	}

	return <MsalProvider instance={b2cMsalInstance}>{content}</MsalProvider>;
}
